import LocationAutocompleteComponent from '../../autocomplete/LocationAutocomplete';
import { Body, BodyLarge } from '../../Typography';
import { Box, Checkbox, MenuItem, Select, styled, TextField, Typography } from '@mui/material';
import { fontWeight } from '../../../theme/typography';
import { HFlex, VFlex } from '../../FlexContainers';
import { palette } from '../../../theme/palette';
import { PrimaryButton } from '../../Buttons';
import { OrderDisplayModalType } from '../../../types/order';
import CrossIcon from '../../icons/CrossIcon';

export const UpdateFormContainer = styled(VFlex)`
  padding: 16px 24px 19px 24px;
  gap: 17px;
`;

export const Form = styled(`form`)`
  display: flex;
  flex-direction: column;

  @media (max-width: 786px) {
    padding: 20px;
  }
`;

export const InputFieldsContainer = styled(VFlex)`
  gap: 17px;

  @media (max-width: 786px) {
    gap: 10px;
  }
`;

export const RowWrapper = styled(HFlex)`
  justify-content: space-between;
  width: 100%;
  gap: 9px;
`;

export const FieldSetWrapper = styled(VFlex)`
  width: 100%;
`;

export const FieldLabel = styled(HFlex, {
  shouldForwardProp: (prop) => prop !== 'containsSubLabel',
})<{
  containsSubLabel?: boolean;
}>`
  align-items: center;
  color: ${palette.custom.darkGrey};
  font-size: 14px;
  font-style: normal;
  font-weight: ${fontWeight.bold};
  line-height: 32px;
  letter-spacing: 0.35px;

  ${(props) =>
    props.containsSubLabel &&
    `
    justify-content: space-between;
  `}
`;

export const FieldLabelWrapper = styled(HFlex, {
  shouldForwardProp: (prop) => prop !== 'isSubLabel',
})<{
  isSubLabel?: boolean;
}>`
  justify-content: unset;

  ${(props) =>
    props.isSubLabel &&
    `
    font-weight: ${fontWeight.regular};
    text-decoration: underline;
    cursor: pointer;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.3px;
  `}
`;

export const RequiredField = styled(FieldLabel)`
  color: #ed1111;
`;

export const ExpanableFieldToggler = styled(Box)`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: auto;
  text-align: right;
  font-size: 12px;
  font-weight: ${fontWeight.regular};
  line-height: 14px;
  letter-spacing: 0.3px;
  text-decoration-line: underline;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

export const InputField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'background' && prop !== 'adornmentBackgroundColor',
})<{
  background?: string | null;
  adornmentBackgroundColor?: boolean | null;
}>`
  width: 100%;
  .MuiInputBase-root {
    border-radius: 5px;
    border: 0.75px solid ${palette.custom.grey};
    background: ${palette.primary.contrastText};
    height: 40px;

    .MuiInputAdornment-root > p {
      color: ${palette.custom.black};
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 0.35px;
      line-height: 32px;
    }

    ${(props) =>
      props.adornmentBackgroundColor &&
      `background: linear-gradient(to right, #f0f0f0 10%, transparent 10%);`}
  }
  .MuiInputBase-input {
    display: flex;
    align-items: center;
    border: 0px;
    padding: 3px 0 3px 12px;

    color: #727276;
    font-size: 14px;
    font-style: normal;
    font-weight: ${fontWeight.regular};
    line-height: 32px;
    letter-spacing: 0.35px;

    /* Hide default calendar icon */
    &::-webkit-calendar-picker-indicator {
      display: none;
    }
  }

  .MuiInputBase-root.Mui-disabled {
    background: ${(props) => (props.background ? props.background : `#e4e8ed80`)};
    color: #776f6f;
  }
  //For firefox
  input[type='number'] {
    -moz-appearance: textfield;
  }
  //For other browsers
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }
  .MuiOutlinedInput-notchedOutline {
    border: 0px;
  }
  &::-webkit-calendar-picker-indicator {
    display: none;
  }
  .MuiInputAdornment-root {
    margin-right: 11px;
    color: #727276;
  }
`;

export const SubmitButton = styled(PrimaryButton)`
  margin-top: 18px;
  box-sizing: border-box;
  padding: 0;
  width: 100%;
  min-height: 43px;
  border-radius: 5px;
  border: 1px solid ${palette.primary.main};
  background: ${palette.primary.main};

  color: ${palette.primary.contrastText};
  text-align: center;

  -webkit-text-stroke-width: 1;
  -webkit-text-stroke-color: ${palette.primary.main};
  font-size: 12px;
  font-style: normal;
  font-weight: ${fontWeight.bold};
  line-height: normal;
  letter-spacing: 0.6px;
  text-transform: uppercase;

  @media (max-width: 786px) {
    margin-top: 35px;
    min-height: 40px;
    width: 100%:
  }
`;

export const DescriptionLabel = styled(Typography)`
  color: ${palette.custom.darkGrey};
  font-size: 12px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  line-height: 14px;
  letter-spacing: 0.3px;
`;

export const Input = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'background',
})<{
  hasError?: boolean;
  background?: string | null;
}>`
  margin-top: 0;
  width: 100%;
  .MuiInputBase-root.Mui-disabled {
    background: ${(props) => (props.background ? props.background : `#e4e8ed80`)};
    color: #776f6f;
  }
  .MuiInputBase-root {
    border-radius: 5px;
    height: 40px;
  }
  input {
    box-sizing: border-box;
    height: 100%;

    color: #727276;
    font-size: 14px;
    font-style: normal;
    font-weight: ${fontWeight.regular};
    line-height: 32px;
    letter-spacing: 0.35px;
  }
  .MuiOutlinedInput-notchedOutline {
    border: 0.75px solid ${palette.custom.grey};
    border-color: ${(props) =>
      props.hasError ? `${palette.error.main}` : `${palette.custom.grey}`};
  }
  .Mui-focused .MuiOutlinedInput-notchedOutline {
    border: 0.75px solid ${palette.custom.grey} !important;
    border-color: ${(props) =>
      props.hasError ? `${palette.error.main} !important` : `${palette.custom.grey} !important`};
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border: 0.75px solid ${palette.custom.grey};
    border-color: ${(props) =>
      props.hasError ? `${palette.error.main}` : `${palette.custom.grey}`};
  }
  .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input {
    padding-left: 12px !important;

    color: #727276;
    font-size: 14px;
    font-style: normal;
    font-weight: ${fontWeight.regular};
    line-height: 32px;
    letter-spacing: 0.35px;
  }
`;

export const textFieldInputProps = {
  sx: {
    '&::placeholder': {
      color: '#727276',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '32px',
      letterSpacing: '0.35px',
      opacity: 1,
    },
  },
};

export const AddressFieldLabelWrapper = styled(HFlex)`
  width: 100%;
`;

export const CoordinateAddresssTogglerText = styled(HFlex)`
  margin-left: auto;
  align-items: center;
  gap: 3px;
  color: ${palette.custom.darkGrey};
  text-align: right;
  font-size: 12px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  line-height: 14px;
  letter-spacing: 0.3px;
`;

export const CoordinateAddresssToggler = styled(CoordinateAddresssTogglerText)`
  text-decoration-line: underline;
  cursor: pointer;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

export const CoordinateFieldValueSetHelperText = styled(Box)`
  margin-top: 5px;
  color: ${palette.custom.greyBorder};
  font-size: 12px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  line-height: 14px;
  letter-spacing: 0.3px;
`;

export const UpdateButtonsWrapper = styled(HFlex)`
  justify-content: space-between;
  gap: 10px;
`;

export const UpdateButton = styled(SubmitButton)`
  flex: 1;
`;

export const RemoveButton = styled(SubmitButton)`
  flex: 0 41.8%;
  background: unset;
  border: 1.25px solid ${palette.primary.main};
  color: ${palette.custom.darkGrey};

  :hover {
    background: unset;
    color: unset;
  }
`;

export const InvalidFieldValue = styled(DescriptionLabel)`
  margin-top: 5px;
  letter-spacing: 0.35px;
  color: ${palette.error.main};
  width: fit-content;
`;

export const BackendError = styled(DescriptionLabel)`
  color: #da344d;
  margin: 16px 0;
`;

export const AddressAutocomplete = styled(LocationAutocompleteComponent, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{
  hasError?: boolean;
}>`
  border-radius: 5px;
  border: ${(props) =>
    props.hasError ? `0.75px solid ${palette.error.main}` : `0.75px solid ${palette.custom.grey}`};
  height: 40px;

  #autocomplete-input {
    color: #727276;
    font-size: 14px;
    font-style: normal;
    font-weight: ${fontWeight.regular};
    line-height: 32px;
    letter-spacing: 0.35px;
    padding-left: 12px;
  }
`;

export const StyledMenuItem = styled(MenuItem)`
  text-wrap: pretty;
  border-radius: 5px;
  margin: 8px;
  padding-left: 14px;
  word-break: break-word;
  max-width: 100%;
  overflow: auto;

  &.Mui-selected,
  &.Mui-selected:hover,
  &.Mui-focusVisible {
    background: ${palette.custom.lightGrey};
  }
`;

export const MenuItemWrapper = styled(VFlex)``;

export const MenuItemLabel = styled(Box)`
  color: #727276;
  font-size: 14px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  line-height: 28px;
  letter-spacing: 0.35px;
`;

export const MenuItemDescription = styled(Typography)`
  color: ${palette.custom.darkGrey};
  font-size: 12px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  line-height: 18px;
  letter-spacing: 0.3px;
`;

export const UserSelector = styled(Select)`
  height: 40px;
  margin: 0px;
  text-transform: capitalize;
  font-size: 14px;
  // .MuiOutlinedInput-notchedOutline {
  //   border: 0px;
  // }
  .MuiSelect-icon {
    right: 0;
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    font-size: 12px;
    color: ${palette.collection.grey.d750};
  }
`;

export const Option = styled(MenuItem)`
  font-size: 14px;
  max-width: 450px;
  display: flex;
  flex-direction: column;
  align-items: baseline;
  flex-wrap: wrap;
  :hover,
  &.Mui-selected:hover {
    background-color: ${palette.custom.lightGrey} !important;
  }
  &.Mui-selected {
    background-color: ${palette.custom.grey100} !important;
  }
`;

export const OptionLabel = styled(Body)`
  font-size: 14px;
  text-transform: capitalize;
  font-weight: ${fontWeight.bold};
`;

export const OptionDescription = styled(Body)`
  font-size: 12px;
  white-space: pre-wrap;
`;

export const InputOptionRow = styled(HFlex)`
  gap: 10px;
  align-items: center;
`;

export const StyledCheckboxContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
`;

export const StyledCheckbox = styled(Checkbox)`
  max-height: 100%;
  max-width: 100%;
`;

export const InputOptionLabel = styled(Typography)`
  color: ${palette.custom.darkGrey};
  font-size: 14px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  line-height: 32px;
  letter-spacing: 0.35px;
`;

export const TextArea = styled('textarea')`
  font-size: 1rem;
  margin-top: 0;
  min-height: 100px;
  height: 128px;
  width: 100%;
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  box-sizing: border-box;
  font-family: trade-gothic-next, sans-serif;
  :focus {
    outline: 2px solid black;
  }
`;

export const SmallTextArea = styled(TextArea)`
  min-height: unset;
  height: 48px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.4px;
`;

export const BottomsheetContentWrapper = styled(Box)``;

export const BottomsheetEquipmentContentWrapper = styled(Box)`
  padding: 0 22px;
`;

export const BottomSheetLabel = styled(BodyLarge)`
  font-weight: ${fontWeight.bold};
  margin-bottom: 0px;
`;

export const BottomSheetTopBar = styled(HFlex)`
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 15px;
  padding: 0px 20px;
`;

export const CloseIcon = styled(CrossIcon)`
  color: ${palette.custom.grey};
  width: 21px;
  height: 21px;
  object-fit: contain;
`;

export const CloseButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'cartModalType',
})<{
  cartModalType: OrderDisplayModalType;
}>`
  height: 25px;
  width: 25px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${palette.primary.main};
  position: ${(props) =>
    props.cartModalType == OrderDisplayModalType.CENTERED_MODAL ? 'absolute' : 'relative'};
  top: ${(props) => (props.cartModalType == OrderDisplayModalType.CENTERED_MODAL ? '16px' : '0px')};
  right: ${(props) =>
    props.cartModalType == OrderDisplayModalType.CENTERED_MODAL ? '18px' : '0px'};
`;

export const EquipmentDetails = styled(VFlex, {
  shouldForwardProp: (prop) => prop !== 'bottomPadding',
})<{
  bottomPadding?: boolean;
}>`
  font-size: 14px;
  font-style: normal;
  font-weight: ${fontWeight.regular};
  line-height: 20px;
  letter-spacing: 0.35px;
  padding-bottom: ${(props) => (props.bottomPadding ? '18px' : '0px')};
`;

export const EquipmentDetailsHeader = styled(Box)`
  font-weight: ${fontWeight.bold};
`;

export const EquipmentDetailsSubHeader = styled(Box)``;

export const LineBreak = styled(Box)`
  height: 1px;
  width: 100%;
  background-color: ${palette.custom.grey};
`;
